// Quiz Taking Component - Student quiz interface
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { quizService } from "../../../services/quizService";
import { Quiz, QuizAttempt, Question } from "../../../types/quiz";
import QuizTimer from "./QuizTimer";
import { toast } from "react-hot-toast";

interface StudentAnswer {
  questionId: string;
  questionType: string;
  response: any;
  answeredAt?: Date;
}

interface QuizTakingProps {
  quizId?: string; // Optional quizId prop for inline usage
  onSubmit?: (attempt: QuizAttempt) => void;
  preview?: boolean; // when true, load quiz for preview (no attempt/start)
}

const QuizTaking: React.FC<QuizTakingProps> = ({
  quizId: propQuizId,
  onSubmit,
  preview,
}) => {
  const { quizId: paramQuizId } = useParams<{ quizId: string }>();
  const quizId = propQuizId || paramQuizId;
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, StudentAnswer>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

  useEffect(() => {
    if (quizId) {
      initializeQuiz();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]);

  const initializeQuiz = async () => {
    try {
      setLoading(true);
      // In preview mode load teacher-facing quiz data without starting an attempt
      if (preview) {
        const quizData = await quizService.getQuiz(quizId!);
        setQuiz(quizData);

        // Initialize answers structure for UI rendering only
        const initialAnswers: Record<string, StudentAnswer> = {};
        (quizData.questions || []).forEach((question: Question) => {
          initialAnswers[question._id!] = {
            questionId: question._id!,
            questionType: question.type,
            response: getInitialResponse(question),
          };
        });
        setAnswers(initialAnswers);
      } else {
        const quizData = await quizService.getQuizForStudent(quizId!);
        setQuiz(quizData);

        // Start quiz attempt
        const attemptData = await quizService.startQuizAttempt(quizId!);
        setAttempt(attemptData);

        // Load full attempt data to get existing answers
        const fullAttemptData = await quizService.getQuizAttempt(
          attemptData._id!
        );

        // Initialize answers - use existing answers if available
        const initialAnswers: Record<string, StudentAnswer> = {};
        quizData.questions.forEach((question: Question) => {
          const existingAnswer = fullAttemptData.answers?.find(
            (a: any) => a.questionId.toString() === question._id
          );

          initialAnswers[question._id!] = existingAnswer
            ? {
                questionId: question._id!,
                questionType: question.type,
                response: existingAnswer.answer,
                answeredAt: existingAnswer.submittedAt
                  ? new Date(existingAnswer.submittedAt)
                  : undefined,
              }
            : {
                questionId: question._id!,
                questionType: question.type,
                response: getInitialResponse(question),
              };
        });
        setAnswers(initialAnswers);
      }
    } catch (error: any) {
      console.error("Error initializing quiz:", error);
      toast.error("Failed to load quiz");
      if (!preview) navigate("/student/quizzes");
    } finally {
      setLoading(false);
    }
  };

  const getInitialResponse = (question: Question): any => {
    switch (question.type) {
      case "multiple_choice":
      case "true_false":
        return null;
      case "multiple_select":
        return [];
      case "short_answer":
      case "essay":
        return "";
      case "fill_blank":
        return (question as any).blanks?.map(() => "") || [];
      case "matching":
        return [];
      case "ordering":
        return (
          (question as any).items?.map((item: any, index: number) => ({
            item,
            order: index,
          })) || []
        );
      case "numeric":
        return "";
      default:
        return null;
    }
  };

  const updateAnswer = (questionId: string, response: any) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        response,
        answeredAt: new Date(),
      },
    }));

    // Auto-save progress
    saveProgress();
  };

  const saveProgress = async () => {
    // In preview mode do not save progress to backend
    if (preview) return;

    if (!attempt || !quiz) return;

    try {
      // Use submitAnswer method for individual answers
      const currentAnswer = answers[quiz.questions[currentQuestionIndex]._id!];
      if (currentAnswer) {
        await quizService.submitAnswer(
          attempt._id!,
          currentAnswer.questionId,
          currentAnswer.response
        );
      }
    } catch (error) {
      console.error("Error saving progress:", error);
    }
  };

  function handleTimeUp() {
    if (!submitting) {
      toast.error("Time is up! Submitting your quiz automatically.");
      handleSubmitQuiz();
    }
  }

  const handlePreviousQuestion = async () => {
    if (currentQuestionIndex > 0) {
      // Save current answer before navigating
      await saveCurrentAnswer();
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNextQuestion = async () => {
    if (!isLastQuestion) {
      // Save current answer before navigating
      await saveCurrentAnswer();
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const saveCurrentAnswer = async () => {
    if (preview || !attempt || !currentQuestion) return;

    const currentAnswer = answers[currentQuestion._id!];
    if (currentAnswer) {
      try {
        await quizService.submitAnswer(
          attempt._id!,
          currentAnswer.questionId,
          currentAnswer.response
        );
        // Update the answer with timestamp
        setAnswers((prev) => ({
          ...prev,
          [currentQuestion._id!]: {
            ...currentAnswer,
            answeredAt: new Date(),
          },
        }));
      } catch (error) {
        console.error("Error saving answer:", error);
        toast.error("Failed to save answer");
      }
    }
  };

  const handleSubmitQuiz = async () => {
    if (preview) {
      toast("Preview mode: quiz not submitted.");
      return;
    }

    if (!attempt || !quiz) return;

    setSubmitting(true);
    try {
      // Save all current answers before submitting
      for (const question of quiz.questions) {
        const currentAnswer = answers[question._id!];
        if (currentAnswer) {
          try {
            await quizService.submitAnswer(
              attempt._id!,
              currentAnswer.questionId,
              currentAnswer.response
            );
          } catch (error) {
            console.error(
              `Error saving answer for question ${question._id}:`,
              error
            );
          }
        }
      }

      const submissionData = await quizService.submitQuizAttempt(attempt._id!);

      // Clear timer state from localStorage
      const storageKey = `quiz-timer-${quizId}-${attempt._id}`;
      localStorage.removeItem(storageKey);

      toast.success("Quiz submitted successfully!");

      if (onSubmit) {
        onSubmit(submissionData);
      } else {
        navigate(
          `/student/elearning/quizzes/${quizId}/results/${submissionData._id}`,
          {
            state: { attempt: submissionData },
          }
        );
      }
    } catch (error: any) {
      console.error("Error submitting quiz:", error);
      toast.error("Failed to submit quiz");
      setSubmitting(false);
    }
  };

  const renderQuestion = (question: Question) => {
    const answer = answers[question._id!];

    switch (question.type) {
      case "multiple_choice":
      case "true_false":
        return (
          <div className="space-y-2 sm:space-y-3">
            {(question as any).options?.map((choice: string, index: number) => (
              <label
                key={index}
                className="flex items-center space-x-2 sm:space-x-3 p-2.5 sm:p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer touch-manipulation"
              >
                <input
                  type="radio"
                  name={`question-${question._id}`}
                  value={choice}
                  checked={answer?.response === choice}
                  onChange={(e) => updateAnswer(question._id!, e.target.value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="flex-1 text-sm sm:text-base">{choice}</span>
              </label>
            ))}
          </div>
        );

      case "multiple_select":
        return (
          <div className="space-y-3">
            {(question as any).options?.map((choice: string, index: number) => (
              <label
                key={index}
                className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  value={choice}
                  checked={answer?.response?.includes(choice)}
                  onChange={(e) => {
                    const currentResponse = answer?.response || [];
                    const newResponse = e.target.checked
                      ? [...currentResponse, choice]
                      : currentResponse.filter((c: string) => c !== choice);
                    updateAnswer(question._id!, newResponse);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="flex-1">{choice}</span>
              </label>
            ))}
          </div>
        );

      case "short_answer":
        return (
          <input
            type="text"
            value={answer?.response || ""}
            onChange={(e) => updateAnswer(question._id!, e.target.value)}
            placeholder="Enter your answer..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
            maxLength={(question as any).maxLength}
          />
        );

      case "essay":
        return (
          <div className="space-y-2">
            <textarea
              value={answer?.response || ""}
              onChange={(e) => updateAnswer(question._id!, e.target.value)}
              placeholder="Write your essay here..."
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
              maxLength={(question as any).maxLength}
            />
            {(question as any).maxLength && (
              <div className="text-sm text-gray-500 text-right">
                {(answer?.response || "").length} /{" "}
                {(question as any).maxLength} characters
              </div>
            )}
          </div>
        );

      case "fill_blank":
        const text = (question as any).question || "";
        const blanks = (question as any).blanks || [];
        const responses = answer?.response || [];

        return (
          <div className="space-y-4">
            <div className="text-lg leading-relaxed">
              {text.split("___").map((part: string, index: number) => (
                <React.Fragment key={index}>
                  {part}
                  {index < blanks.length && (
                    <input
                      type="text"
                      value={responses[index] || ""}
                      onChange={(e) => {
                        const newResponses = [...responses];
                        newResponses[index] = e.target.value;
                        updateAnswer(question._id!, newResponses);
                      }}
                      className="inline-block mx-1 px-2 py-1 border-b-2 border-blue-500 bg-transparent focus:outline-none min-w-[100px]"
                      placeholder="Answer"
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        );

      case "numeric":
        return (
          <input
            type="number"
            value={answer?.response || ""}
            onChange={(e) => updateAnswer(question._id!, e.target.value)}
            placeholder="Enter a number..."
            step="any"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case "comprehension":
        const comprehensionData = question as any;
        const comprehensionAnswers = answer?.response || {};

        return (
          <div className="space-y-4">
            {/* Reading Passage */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h3 className="text-base font-semibold text-blue-900 mb-2">
                Reading Passage
              </h3>
              <div className="text-gray-800 leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
                {comprehensionData.passage}
              </div>
            </div>

            {/* Sub-questions */}
            <div className="space-y-4">
              {comprehensionData.subQuestions?.map(
                (subQ: any, subIndex: number) => {
                  const subAnswer = comprehensionAnswers[subQ._id] || "";

                  return (
                    <div
                      key={subQ._id || subIndex}
                      className="border border-gray-200 rounded-lg p-3"
                    >
                      <div className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                          {subIndex + 1}
                        </span>
                        <div className="flex-1">
                          <p className="text-gray-800 mb-2 text-sm sm:text-base">
                            {subQ.question}
                          </p>

                          {subQ.type === "multiple_choice" && (
                            <div className="space-y-2">
                              {subQ.options?.map(
                                (choice: string, choiceIndex: number) => (
                                  <label
                                    key={choiceIndex}
                                    className="flex items-center space-x-2 cursor-pointer"
                                  >
                                    <input
                                      type="radio"
                                      name={`comprehension-${question._id}-${subIndex}`}
                                      value={choice}
                                      checked={subAnswer === choice}
                                      onChange={(e) => {
                                        const newAnswers = {
                                          ...comprehensionAnswers,
                                        };
                                        newAnswers[subQ._id] = e.target.value;
                                        updateAnswer(question._id!, newAnswers);
                                      }}
                                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-sm">{choice}</span>
                                  </label>
                                )
                              )}
                            </div>
                          )}

                          {subQ.type === "true_false" && (
                            <div className="space-y-2">
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`comprehension-${question._id}-${subIndex}`}
                                  value="True"
                                  checked={subAnswer === "True"}
                                  onChange={(e) => {
                                    const newAnswers = {
                                      ...comprehensionAnswers,
                                    };
                                    newAnswers[subQ._id] = "True";
                                    updateAnswer(question._id!, newAnswers);
                                  }}
                                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-sm">True</span>
                              </label>
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`comprehension-${question._id}-${subIndex}`}
                                  value="False"
                                  checked={subAnswer === "False"}
                                  onChange={(e) => {
                                    const newAnswers = {
                                      ...comprehensionAnswers,
                                    };
                                    newAnswers[subQ._id] = "False";
                                    updateAnswer(question._id!, newAnswers);
                                  }}
                                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-sm">False</span>
                              </label>
                            </div>
                          )}

                          {(subQ.type === "short_answer" ||
                            subQ.type === "essay") && (
                            <textarea
                              value={subAnswer}
                              onChange={(e) => {
                                const newAnswers = { ...comprehensionAnswers };
                                newAnswers[subQ._id] = e.target.value;
                                updateAnswer(question._id!, newAnswers);
                              }}
                              placeholder={
                                subQ.type === "short_answer"
                                  ? "Enter your answer..."
                                  : "Write your essay here..."
                              }
                              rows={subQ.type === "essay" ? 6 : 2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          )}

                          {subQ.type === "fill_blank" && (
                            <div className="text-gray-800">
                              {subQ.question
                                .split("___")
                                .map((part: string, blankIndex: number) => (
                                  <React.Fragment key={blankIndex}>
                                    {part}
                                    {blankIndex <
                                      (subQ.correctAnswers?.length || 0) && (
                                      <input
                                        type="text"
                                        value={
                                          (subAnswer &&
                                            subAnswer[blankIndex]) ||
                                          ""
                                        }
                                        onChange={(e) => {
                                          const newAnswers = {
                                            ...comprehensionAnswers,
                                          };
                                          const currentSubAnswer =
                                            newAnswers[subQ._id] || [];
                                          currentSubAnswer[blankIndex] =
                                            e.target.value;
                                          newAnswers[subQ._id] =
                                            currentSubAnswer;
                                          updateAnswer(
                                            question._id!,
                                            newAnswers
                                          );
                                        }}
                                        className="inline-block mx-1 px-2 py-1 border-b-2 border-blue-500 bg-transparent focus:outline-none min-w-[100px]"
                                        placeholder="Answer"
                                      />
                                    )}
                                  </React.Fragment>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="text-gray-500 text-center p-8">
            Question type not supported in taking mode
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!quiz || (!attempt && !preview)) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Quiz Not Found
        </h3>
        <p className="text-gray-600">
          The quiz you're looking for doesn't exist or is not available.
        </p>
      </div>
    );
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === quiz.questions.length - 1;
  const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-1 sm:py-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3">
            <div>
              <h1 className="text-sm sm:text-lg font-bold text-gray-900 truncate max-w-[200px] sm:max-w-none">
                {quiz.title}
              </h1>
              <p className="text-xs text-gray-600">
                Question {currentQuestionIndex + 1} of {quiz.questions.length}
              </p>
            </div>
          </div>
          <QuizTimer
            initialTime={quiz.timeLimit ? quiz.timeLimit * 60 : 0}
            onTimeUp={handleTimeUp}
            isRunning={!preview && !!attempt}
            quizId={quizId}
            attemptId={attempt?._id}
          />
        </div>

        {/* Progress Bar */}
        <div className="mt-1 sm:mt-2">
          <div className="bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-2 sm:p-4">
        <div className="max-w-4xl mx-auto">
          {/* Question */}
          <div className="bg-white rounded-lg border shadow-sm p-3 sm:p-6">
            <div className="mb-2 sm:mb-4">
              <div className="flex items-start justify-between mb-2 sm:mb-3">
                <h2 className="text-sm sm:text-lg font-semibold text-gray-900 flex-1">
                  {(currentQuestion as any).question}
                </h2>
                <span className="text-xs text-gray-500 ml-2">
                  {currentQuestion.points}{" "}
                  {currentQuestion.points === 1 ? "point" : "points"}
                </span>
              </div>

              {(currentQuestion as any).description && (
                <p className="text-gray-600 mb-2 sm:mb-3 text-sm">
                  {(currentQuestion as any).description}
                </p>
              )}
            </div>

            {renderQuestion(currentQuestion)}
          </div>

          {/* Navigation */}
          <div className="bg-white border-t border-gray-200 p-2 sm:p-3">
            <div className="flex flex-col gap-3">
              {/* Question Navigation Boxes */}
              <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                {quiz.questions.map((question, index) => {
                  const isAnswered =
                    answers[question._id]?.response !== undefined &&
                    answers[question._id]?.response !== null &&
                    answers[question._id]?.response !== "";
                  const isCurrent = index === currentQuestionIndex;

                  return (
                    <span
                      key={question._id}
                      onClick={() => setCurrentQuestionIndex(index)}
                      className={`flex items-center gap-1.5 sm:gap-2 rounded-full text-left border transition-colors ${
                        isCurrent
                          ? "bg-blue-100 border-blue-300"
                          : "hover:bg-gray-50 border-gray-200"
                      }`}
                      title={`Question ${index + 1}${
                        isAnswered ? " (Answered)" : ""
                      }`}
                    >
                      <span
                        className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                          isAnswered
                            ? "bg-green-200 text-green-800"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {index + 1}
                      </span>
                    </span>
                  );
                })}
              </div>

              {/* Previous/Next Buttons */}
              <div className="flex justify-between items-center">
                <button
                  onClick={handlePreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                  className="px-3 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm touch-manipulation"
                >
                  ← Previous
                </button>

                {!isLastQuestion ? (
                  <button
                    onClick={handleNextQuestion}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm touch-manipulation"
                  >
                    Next →
                  </button>
                ) : (
                  <button
                    onClick={() => setShowConfirmSubmit(true)}
                    disabled={submitting}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 text-sm touch-manipulation"
                  >
                    {submitting ? "Submitting..." : "Submit Quiz"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmSubmit && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full mx-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                Submit Quiz?
              </h3>
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                Are you sure you want to submit your quiz? You won't be able to
                change your answers after submission.
              </p>
              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
                <button
                  onClick={() => setShowConfirmSubmit(false)}
                  className="px-4 py-3 sm:py-2 text-gray-600 hover:text-gray-800 text-sm sm:text-base touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowConfirmSubmit(false);
                    handleSubmitQuiz();
                  }}
                  className="px-4 py-3 sm:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base touch-manipulation"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizTaking;
